import { useState, useEffect } from 'react'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import Select from 'react-select'
import {
  KeyValuePairs,
  InputContainer,
  GoogleAutocomplete,
  DatePicker,
  Checkbox
} from 'components/ideal-core-ui'
import { fromConstantPropertyToSelectOptions } from 'utils/serializers'
import { APP_CONSTANTS } from 'utils/constants'
import s from './CompanyModal.scss'
import { toSelectValue } from '../serializers'
import { timezoneDate, utcDate } from 'utils/helpers'
import { PaginatedSelect } from 'components/common/PaginatedSelect'
import { toCompaniesSelect } from 'components/Companies/serializers'
import { AutosuggestInput } from 'components/common/AutosuggestInput'

const CompanyModal = ({
  company,
  showCompanyModal,
  toggleCompanyModal,
  validateProperty,
  changeProperty,
  saveCompany,
  showCompanyError,
  showDuplicateError,
  uploadFile,
  onAddressSelectCallback,
  noBidReasons,
  segments,
  companyTypes
}) => {
  const {
    name,
    legalName,
    address,
    address2,
    street2,
    city,
    state,
    country,
    zipcode,
    phone,
    additionalPhone,
    fax,
    website,
    email,
    emailDomain,
    types,
    size,
    preQualified,
    preQualifiedExpDate,
    facebook,
    linkedin,
    twitter,
    instagram,
    youtube,
    noBid,
    noBidReason,
    noBidReasonId,
    noBidReasonDescription,
    description,
    segment,
    parentCompany
  } = company
  const [showReasonDescription, setShowReasonDescription] = useState(false)

  useEffect(() => {
    setShowReasonDescription(noBidReason?.name === 'Other')
  }, [noBidReason])

  return (
    <Modal
      keyboard={false}
      backdrop="static"
      isOpen={showCompanyModal}
      toggle={toggleCompanyModal}
      className={s.companyModal}
      size="xl"
    >
      <ModalHeader toggle={toggleCompanyModal} className={s.modalHeader}>
        {company?.id ? 'Edit Company' : 'Add New Company'}
      </ModalHeader>
      <ModalBody>
        {showCompanyError && <p className={s.error}>All required fields must have a value</p>}
        {showDuplicateError && (
          <p className={s.error}>Another company with the same name already exists</p>
        )}
        <div className={s.wrapper}>
          <div className={s.column}>
            <KeyValuePairs
              pairs={[
                {
                  key: '*Company Name',
                  value: (
                    <AutosuggestInput
                      type="COMPANY"
                      placeholder="Enter Company Name"
                      onChangeCb={(value, suggestions) => {
                        changeProperty(value, 'name')
                        validateProperty(value, suggestions)
                      }}
                      length="3"
                      cy="company_name_input"
                      initialValue={name}
                    />
                  )
                },
                {
                  key: '*Company Legal Name',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Company Legal Name"
                      cb={value => changeProperty(value, 'legalName')}
                      text={legalName}
                      cy="company_legal_name_input"
                    />
                  )
                },
                {
                  key: 'Company Description',
                  value: (
                    <InputContainer
                      type="textarea"
                      text={description}
                      classname={s.description}
                      placeholder="Enter Company Description"
                      cb={value => changeProperty(value, 'description')}
                    />
                  )
                },
                {
                  key: 'Address',
                  value: (
                    <GoogleAutocomplete
                      value={address}
                      onChange={value => changeProperty(value, 'address')}
                      onSelectCallback={onAddressSelectCallback}
                    />
                  )
                },
                {
                  key: 'Street 1',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Street 1"
                      cb={value => changeProperty(value, 'address2')}
                      text={address2}
                    />
                  )
                },
                {
                  key: 'Street 2',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Street 2"
                      cb={value => changeProperty(value, 'street2')}
                      text={street2}
                    />
                  )
                },
                {
                  key: 'City',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter City"
                      cb={value => changeProperty(value, 'city')}
                      text={city}
                    />
                  )
                },
                {
                  key: 'State/Province/Region',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter State/Province/Region"
                      cb={value => changeProperty(value, 'state')}
                      text={state}
                    />
                  )
                },
                {
                  key: 'Zip/Postal Code',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Zip/Postal Code"
                      cb={value => changeProperty(value, 'zipcode')}
                      text={zipcode}
                    />
                  )
                },
                {
                  key: 'Country',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      text={country}
                      placeholder="Enter Country"
                      cb={value => changeProperty(value, 'country')}
                    />
                  )
                },
                {
                  key: 'Phone',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Phone"
                      cb={value => changeProperty(value, 'phone')}
                      text={phone}
                    />
                  )
                },
                {
                  key: 'Additional Phone',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Additional Phone"
                      cb={value => changeProperty(value, 'additionalPhone')}
                      text={additionalPhone}
                    />
                  )
                },
                {
                  key: 'Fax',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Fax"
                      cb={value => changeProperty(value, 'fax')}
                      text={fax}
                    />
                  )
                },
                {
                  key: 'Website http(s)://www.example.com',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Company's website"
                      cb={value => changeProperty(value, 'website')}
                      text={website}
                    />
                  )
                },
                {
                  key: 'Email',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Email"
                      cb={value => changeProperty(value, 'email')}
                      text={email}
                    />
                  )
                },
                {
                  key: 'Email Domain',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Email Domain i.e. fclconstruction.com"
                      cb={value => changeProperty(value, 'emailDomain')}
                      text={emailDomain}
                    />
                  )
                }
              ]}
            />
          </div>
          <div className={s.column}>
            <KeyValuePairs
              pairs={[
                {
                  key: 'Company Type',
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'types')}
                      options={companyTypes}
                      value={types}
                      isMulti
                    />
                  )
                },
                {
                  key: 'Market Segment',
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'segment')}
                      options={segments}
                      value={segment}
                    />
                  )
                },
                {
                  key: 'Company Level',
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'size')}
                      options={fromConstantPropertyToSelectOptions(
                        Object.keys(APP_CONSTANTS.CompanySize),
                        'CompanySize'
                      )}
                      value={size}
                    />
                  )
                },
                {
                  key: 'Parent Company',
                  value: (
                    <PaginatedSelect
                      url="/v1/companies"
                      value={parentCompany}
                      onChange={value => changeProperty(value, 'parentCompany')}
                      serializer={toCompaniesSelect}
                    />
                  )
                },
                {
                  key: 'Pre-Qualified',
                  value: (
                    <div className={s.radioGroup}>
                      <input
                        type="radio"
                        checked={preQualified}
                        onChange={() => changeProperty(true, 'preQualified')}
                      />{' '}
                      Yes
                      <input
                        type="radio"
                        checked={!preQualified}
                        onChange={() => {
                          changeProperty(false, 'preQualified')
                        }}
                        className={s.rightRadio}
                      />{' '}
                      No
                    </div>
                  )
                },
                {
                  key: 'Pre-Qualification Exp Date',
                  value: (
                    <DatePicker
                      value={preQualifiedExpDate && timezoneDate(preQualifiedExpDate)}
                      onChange={value => {
                        const date = value && value !== null ? utcDate(value) : ''
                        changeProperty(date, 'preQualifiedExpDate')
                      }}
                    />
                  )
                },
                {
                  key: 'Do not bid',
                  value: (
                    <Checkbox
                      isChecked={noBid}
                      triggerCheckbox={value => changeProperty(value.target.checked, 'noBid')}
                      name="noBid"
                    />
                  )
                },
                {
                  key: 'Reason for not bidding',
                  value: (
                    <div>
                      <Select
                        onChange={value => {
                          if (value.label === 'Other') {
                            setShowReasonDescription(true)
                          } else {
                            setShowReasonDescription(false)
                            changeProperty('', 'noBidReasonDescription')
                          }
                          changeProperty(value.value, 'noBidReasonId')
                        }}
                        options={noBidReasons}
                        value={toSelectValue(noBidReasonId, noBidReasons)}
                        isDisabled={!noBid}
                      />
                      {showReasonDescription && (
                        <InputContainer
                          type="textarea"
                          placeholder="Reason for not bidding"
                          cb={value => changeProperty(value, 'noBidReasonDescription')}
                          text={noBidReasonDescription}
                          classname={s.noBidDescription}
                        />
                      )}
                    </div>
                  )
                },
                {
                  key: 'Facebook',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Facebook"
                      cb={value => changeProperty(value, 'facebook')}
                      text={facebook}
                    />
                  )
                },
                {
                  key: 'Linkedin',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Linkedin"
                      cb={value => changeProperty(value, 'linkedin')}
                      text={linkedin}
                    />
                  )
                },
                {
                  key: 'Twitter',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Twitter"
                      cb={value => changeProperty(value, 'twitter')}
                      text={twitter}
                    />
                  )
                },
                {
                  key: 'Instagram',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Instagram"
                      cb={value => changeProperty(value, 'instagram')}
                      text={instagram}
                    />
                  )
                },
                {
                  key: 'Youtube',
                  value: (
                    <InputContainer
                      type="text"
                      className={s.nameInput}
                      placeholder="Enter Youtube"
                      cb={value => changeProperty(value, 'youtube')}
                      text={youtube}
                    />
                  )
                },
                {
                  key: 'Company Logo',
                  value: <input type="file" onChange={uploadFile} className={s.fileInput} />
                }
              ]}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button data-cy="save_btn" className={s.saveBtn} onClick={saveCompany}>
          Save
        </Button>
        <Button color="secondary" onClick={toggleCompanyModal}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default CompanyModal
